import { useEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from "@mui/material";
import { AppModal } from "global/components/AppModal";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import notifiers from "global/constants/NotificationConstants";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import strings from "global/constants/StringConstants";
import CustomDropzone from "global/components/CustomDropzone/CustomDropzone";
import DropzoneBody, {
  dropzoneSurfaceSx,
} from "global/components/CustomDropzone/DropzoneBody";
import {
  addNewSupportTicket,
  getProcessDefinitions,
  uploadSupportTicketAttachment,
} from "./CreateTicketService";
import { ChevronDownIcon } from "../components/TicketIcons";
import createTicketTokens from "./CreateTicket.tokens";
import {
  counterSx,
  editorShellSx,
  fieldColumnSx,
  fieldRowSx,
  fieldSx,
  formSx,
  helperTextSx,
  labelSx,
  selectIconSx,
  selectMenuSx,
  selectPlaceholderSx,
  selectValueSx,
  stagedFileChipSx,
  stagedFilesSx,
  supportedFormatsSx,
} from "./CreateTicket.styles";
import useQuillAccessibility from "utils/quillAccessibility";
import { focusFirstInvalidField } from "utils/accessibility";

const t = createTicketTokens;

/** The four groups the design draws: headings, marks, lists, link. */
const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
  ],
  clipboard: {
    matchVisual: false,
  },
};

const formats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "link",
];

const ACCEPTED_FILES = [
  ".jpeg",
  ".jpg",
  ".png",
  ".doc",
  ".pdf",
  ".csv",
  ".xls",
  ".xlsx",
];

const SUMMARY_MAX_LENGTH = 100;
const MAX_ATTACHMENTS = 10;
const PRIORITIES = ["High", "Medium", "Low"];

const emptyForm = () => ({
  process: { value: "", error: "" },
  priority: { value: "", error: "" },
  title: { value: "", error: "" },
  description: { value: "", error: "" },
});

interface CustomProps {
  openModel: boolean;
  setOpenModel: Function;
  handleDialogClose: Function;
  getTableData: any;
  fetchMetrics: any;
}

const PROCESS_FIELD_ID = "ticket_process";
const PRIORITY_FIELD_ID = "ticket_priority";
const TITLE_FIELD_ID = "ticket_title";

/** Ids wiring the Quill editor to its visible label and its error text. */
const DESCRIPTION_LABEL_ID = "ticket-description-label";
const DESCRIPTION_ERROR_ID = "ticket-description-error";
const DESCRIPTION_LIMIT_ID = "ticket-description-limit-error";

/**
 * "Create Ticket" dialog: category and priority side by side, a one-line
 * summary, the rich-text description and the dashed attachment strip, closed by
 * the Cancel / Create pair.
 */
const CreateTicket = (props: CustomProps) => {
  const [process, setProcess] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filePath, setFilePath] = useState<string[]>([]);
  const [uploadFileName, setUploadFileName] = useState<any>([]);
  const [ticketForm, setTicketForm] = useState<any>(emptyForm());
  const quillRef = useRef<ReactQuill>(null);

  const descriptionErrorId =
    !isTruthy(ticketForm.description.value) && ticketForm.description.error
      ? DESCRIPTION_ERROR_ID
      : isTruthy(ticketForm.description.value) &&
          ticketForm.description.value.length >= strings.USER_DESCRIPTION_LIMIT
        ? DESCRIPTION_LIMIT_ID
        : undefined;

  // Quill's contenteditable surface carries no role, name or required state of
  // its own, and its toolbar buttons are unlabelled (WCAG 1.3.1 / 4.1.2).
  useQuillAccessibility(quillRef, {
    labelledBy: DESCRIPTION_LABEL_ID,
    describedBy: descriptionErrorId,
    required: true,
    // The editor only exists in the DOM while the dialog is open.
    enabled: props.openModel,
    toolbarLabel: "Description formatting",
  });

  useEffect(() => {
    if (props.openModel) {
      loadProcess();
    }
  }, [props.openModel]);

  useEffect(() => {
    setTicketForm(emptyForm());
    setFilePath([]);
    setUploadFileName([]);
  }, [props.openModel]);

  const handleOnChange = (event: any) => {
    setTicketForm({
      ...ticketForm,
      [event.target.name]: {
        ...ticketForm[event.target.name],
        value: event.target.value,
        error: "",
      },
    });
  };

  const handleContentChange = (content: string) => {
    setTicketForm({
      ...ticketForm,
      description: {
        value: content,
        error: "",
      },
    });
  };

  const handleValidation = () => {
    let errors = ticketForm;
    let isValid = true;
    const process = ticketForm.process.value;
    const title = ticketForm.title.value.trim();
    const description = ticketForm.description.value.trim();
    const priority = ticketForm.priority.value.trim();
    if (!process && !title && !description && !priority) {
      // Set the fields as error true
      errors.process.error = "Please select category";
      errors.title.error = "Please enter summary";
      errors.priority.error = "Please select priority";
      errors.description.error = "Please enter description";
      isValid = false;
    } else if (!process) {
      errors.process.error = "Please select category";
      isValid = false;
    } else if (!priority) {
      errors.priority.error = "Please select priority";
      isValid = false;
    } else if (!title) {
      errors.title.error = "Please enter summary";
      isValid = false;
    } else if (!description) {
      errors.description.error = "Please enter description";
      isValid = false;
    } else if (filePath.length > MAX_ATTACHMENTS) {
      isValid = false;
      openErrorNotification(
        `You can upload maximum ${MAX_ATTACHMENTS} files only.`,
      );
    }
    setTicketForm({ ...errors });
    if (!isValid) {
      focusFirstInvalidField([
        errors.process.error && PROCESS_FIELD_ID,
        errors.priority.error && PRIORITY_FIELD_ID,
        errors.title.error && TITLE_FIELD_ID,
        errors.description.error &&
          (quillRef.current?.getEditor().root ?? null),
      ]);
    }
    return isValid;
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      if (
        handleValidation() &&
        ticketForm?.description?.value?.length < strings.USER_DESCRIPTION_LIMIT
      ) {
        const body = {
          process: ticketForm.process.value,
          name: ticketForm.title.value,
          description: ticketForm.description.value,
          docUrls: filePath,
          priority: ticketForm.priority.value,
        };
        await addNewSupportTicket(body);
        openSuccessNotification("Your Response has been submitted");
        setTicketForm(emptyForm());
        setFilePath([]);
        setUploadFileName([]);
        setIsLoading(false);
        props.setOpenModel(false);
        await props.fetchMetrics();
        await props.getTableData();
      } else {
        setIsLoading(false);
      }
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR,
      );
    }
  };

  const handleDeleteFile = (index: any) => {
    setUploadFileName(
      uploadFileName.filter((item: any, i: number) => i !== index),
    );
    setFilePath(filePath.filter((item: any, i: number) => i !== index));
  };

  const uploadFile = async (event: any) => {
    try {
      let fileNameArray: any[] = [...uploadFileName];
      let uploadFilePath: any[] = [...filePath];
      const fileData = event?.filter((data: any) => {
        return !uploadFileName?.includes(data?.file?.name);
      });
      const isExits = checkIsExitsFileName(fileData);
      if (fileData.length + uploadFilePath.length > MAX_ATTACHMENTS) {
        return openErrorNotification(
          `Maximum ${MAX_ATTACHMENTS} file can be added.`,
        );
      }
      if (!isExits) {
        for (const element of fileData) {
          setIsLoading(true);
          let fileNames = element?.name;
          const data = new FormData();
          data.append("file", element);
          const [res] = await Promise.all([
            uploadSupportTicketAttachment(data, "new"),
          ]);
          uploadFilePath.push(res?.path);
          fileNameArray.push(fileNames);
          setIsLoading(false);
        }
        setFilePath(uploadFilePath);
        setUploadFileName(fileNameArray);
      }
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR,
      );
    }
  };

  const checkIsExitsFileName = (files: any) => {
    const data: any = [];
    return files.every((item: any) => {
      if (uploadFileName?.includes!(item?.file?.name)) {
        data.push(item?.file?.name);
      }
      return uploadFileName?.includes(item?.file?.name);
    });
  };

  const loadProcess = async () => {
    try {
      setIsLoading(true);
      const response = await getProcessDefinitions();
      setProcess([...response]);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR,
      );
    }
  };

  /** A field only reports its error while it is still empty. */
  const errorFor = (field: string) =>
    !isTruthy(ticketForm[field].value) && ticketForm[field].error
      ? ticketForm[field].error
      : "";

  const getSelect = (
    name: "process" | "priority",
    label: string,
    placeholder: string,
    options: string[],
  ) => {
    const error = errorFor(name);
    return (
      <Box sx={fieldColumnSx}>
        <InputLabel
          required
          id={`ticket_${name}_label`}
          htmlFor={`ticket_${name}`}
          sx={labelSx}
        >
          {label}
        </InputLabel>
        <Select
          labelId={`ticket_${name}_label`}
          name={name}
          value={ticketForm[name].value}
          onChange={handleOnChange}
          displayEmpty
          error={Boolean(error)}
          SelectDisplayProps={{
            id: `ticket_${name}`,
            "aria-required": true,
            "aria-invalid": Boolean(error) || undefined,
            ...(error ? { "aria-describedby": `ticket_${name}_error` } : {}),
          }}
          IconComponent={ChevronDownIcon}
          renderValue={(selected: any) =>
            selected ? (
              selected
            ) : (
              <Box component="span" sx={selectPlaceholderSx}>
                {placeholder}
              </Box>
            )
          }
          sx={{
            ...fieldSx(Boolean(error)),
            "& .MuiSelect-select": selectValueSx,
            "& .MuiSvgIcon-root": selectIconSx,
          }}
          MenuProps={{ PaperProps: { sx: selectMenuSx } }}
        >
          {options.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
        {error && (
          <FormHelperText
            id={`ticket_${name}_error`}
            error
            role="alert"
            sx={helperTextSx}
          >
            {error}
          </FormHelperText>
        )}
      </Box>
    );
  };

  const getSummaryField = () => {
    const error = errorFor("title");
    return (
      <Box>
        <InputLabel required htmlFor="ticket_title" sx={labelSx}>
          Summary
        </InputLabel>
        <OutlinedInput
          id="ticket_title"
          name="title"
          value={ticketForm.title.value}
          onChange={handleOnChange}
          placeholder="One line describing the issue"
          error={Boolean(error)}
          aria-describedby={error ? "ticket_title_error" : undefined}
          inputProps={{
            minLength: 5,
            maxLength: SUMMARY_MAX_LENGTH,
            "aria-required": true,
          }}
          endAdornment={
            <Box component="span" sx={counterSx}>
              {ticketForm.title.value.length} / {SUMMARY_MAX_LENGTH}
            </Box>
          }
          sx={fieldSx(Boolean(error))}
        />
        {error && (
          <FormHelperText
            id="ticket_title_error"
            error
            role="alert"
            sx={helperTextSx}
          >
            {error}
          </FormHelperText>
        )}
      </Box>
    );
  };

  const getDescriptionField = () => {
    const error = errorFor("description");
    const overLimit =
      isTruthy(ticketForm.description.value) &&
      ticketForm.description.value.length >= strings.USER_DESCRIPTION_LIMIT;
    return (
      <Box>
        <InputLabel required id={DESCRIPTION_LABEL_ID} sx={labelSx}>
          Description
        </InputLabel>
        <Box sx={editorShellSx(Boolean(error) || overLimit)}>
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={ticketForm.description.value}
            onChange={handleContentChange}
            formats={formats}
            modules={modules}
            placeholder="Include steps to reproduce, what you expected, and what happened instead."
          />
        </Box>
        {error && (
          <FormHelperText
            id={DESCRIPTION_ERROR_ID}
            error
            role="alert"
            sx={helperTextSx}
          >
            {error}
          </FormHelperText>
        )}
        {overLimit && (
          <FormHelperText
            id={DESCRIPTION_LIMIT_ID}
            error
            role="alert"
            sx={helperTextSx}
          >
            {`Description cannot be more than ${strings.USER_DESCRIPTION_LIMIT} characters`}
          </FormHelperText>
        )}
      </Box>
    );
  };

  /** Same drop surface as the Dashboard and every other upload in the app. */
  const getAttachments = () => (
    <Box>
      <InputLabel sx={labelSx}>Attachments</InputLabel>
      <CustomDropzone
        uniqueId="create-ticket"
        sx={dropzoneSurfaceSx()}
        ariaLabel="Attach a file to this ticket"
        onAdd={(file: any) => uploadFile(file)}
        maxFileSize={t.dropzone.maxFileSizeBytes}
        showAlerts={false}
        multipleFileUpload={false}
        acceptedFiles={ACCEPTED_FILES}
        fileNames={uploadFileName}
        innerBody={
          <DropzoneBody
            title="Drop a file to attach"
            formatsText={t.dropzone.maxFileSizeLabel}
          />
        }
      />

      {uploadFileName?.length > 0 && (
        <Box sx={stagedFilesSx}>
          {uploadFileName.map((item: any, index: number) => (
            <Chip
              key={`${item}-${index}`}
              label={item}
              size="small"
              // MUI makes the chip a focusable role="button" whose only action
              // is Delete/Backspace; saying so is the only way that is
              // discoverable (WCAG 4.1.2).
              aria-label={`${item}, attachment. Press Delete to remove.`}
              sx={stagedFileChipSx}
              onDelete={() => handleDeleteFile(index)}
            />
          ))}
        </Box>
      )}

      <Typography component="span" sx={supportedFormatsSx}>
        Supported formats: jpeg, jpg, png, pdf, doc, csv, xls, xlsx
      </Typography>
    </Box>
  );

  return (
    <>
      <AppModal
        open={props.openModel}
        onClose={() => props.handleDialogClose()}
        title="Create Ticket"
        subtitle="Tell us what's wrong — we usually reply within a business day."
        // A multi-field form — the design system's L width.
        size="lg"
        busy={isLoading}
        // The draft is destructive to lose, so a stray backdrop click must not
        // dismiss it. Esc stays live — it is the expected keyboard way out, and
        // `busy` already locks it while a submit is in flight.
        disableBackdropClose
        primaryLabel="Create Ticket"
        onPrimary={() => handleSubmit()}
        primaryLoading={isLoading}
        cancelLabel="Cancel"
        onCancel={() => props.handleDialogClose()}
      >
        <Box sx={formSx}>
          <Box sx={fieldRowSx}>
            {getSelect(
              "process",
              "Category",
              "Select category",
              process.map((item: any) => item.name),
            )}
            {getSelect("priority", "Priority", "Select priority", PRIORITIES)}
          </Box>

          {getSummaryField()}
          {getDescriptionField()}
          {getAttachments()}
        </Box>
      </AppModal>

      <CustomLoader isLoading={isLoading} />
    </>
  );
};

export default CreateTicket;
